import { useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react'
import { Botao } from '../../components/Botao'
import { TEXTOS } from '../../lib/glossario'

const LIMITE = 1000

/**
 * Onde se escreve a pergunta.
 *
 * Enter envia e Shift+Enter quebra a linha — o costume de toda conversa —, e a dica fica escrita
 * embaixo, porque o público não é de TI e não tem por que adivinhar. A caixa cresce com o texto até
 * um teto, e o contador só aparece perto do limite: antes disso, ele é ruído.
 *
 * @param props o texto, quem o muda, se dá para enviar, e o envio
 * @returns o formulário
 */
export function CaixaDePergunta({
  texto,
  aoMudar,
  aoEnviar,
  desabilitada,
  ocupada,
}: {
  texto: string
  aoMudar: (texto: string) => void
  aoEnviar: () => void
  desabilitada: boolean
  ocupada: boolean
}) {
  const t = TEXTOS.consulta
  const caixa = useRef<HTMLTextAreaElement>(null)
  const vazio = texto.trim().length === 0

  useEffect(() => {
    const elemento = caixa.current
    if (elemento) {
      elemento.style.height = 'auto'
      elemento.style.height = `${Math.min(elemento.scrollHeight, 200)}px`
    }
  }, [texto])

  function enviar(evento?: FormEvent) {
    evento?.preventDefault()
    if (!vazio && !desabilitada && !ocupada) {
      aoEnviar()
    }
  }

  function aoTeclar(evento: KeyboardEvent<HTMLTextAreaElement>) {
    if (evento.key === 'Enter' && !evento.shiftKey && !evento.nativeEvent.isComposing) {
      evento.preventDefault()
      enviar()
    }
  }

  return (
    <form
      onSubmit={enviar}
      className="rounded-2xl bg-white p-2 shadow-cartao-alto ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-niteroi-500"
    >
      <label htmlFor="pergunta-ao-contrato" className="sr-only">
        {t.campo}
      </label>
      <div className="flex items-end gap-2">
        <textarea
          id="pergunta-ao-contrato"
          ref={caixa}
          rows={1}
          value={texto}
          maxLength={LIMITE}
          disabled={desabilitada}
          placeholder={t.placeholder}
          onChange={(evento) => aoMudar(evento.currentTarget.value)}
          onKeyDown={aoTeclar}
          className="max-h-[200px] min-h-[2.75rem] flex-1 resize-none border-0 bg-transparent px-3 py-2.5 text-[0.95rem] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
        />
        <Botao aparencia="primario" type="submit" disabled={vazio || desabilitada || ocupada} className="mb-0.5 mr-0.5">
          {t.enviar}
        </Botao>
      </div>
      <p className="flex justify-between px-3 pb-0.5 pt-1 text-xs text-slate-400">
        <span className="hidden sm:inline">{t.enviarAjuda}</span>
        {texto.length > LIMITE * 0.8 && (
          <span className="tabular-nums">
            {texto.length}/{LIMITE}
          </span>
        )}
      </p>
    </form>
  )
}
